import { Checkbox, GridCellParams, GridRenderCellParams } from "@platform/style";
import * as React from "react";
import { Dispatch, SetStateAction } from "react";
import { WorkItemStatusChip } from "./WorkItemStatusChip";
import { WorkItemRowMenu } from "./WorkItemRowMenu";
import { format, parseISO } from "date-fns";
import { CustomFreeFormOperators } from "./CustomFreeFormOperators";
import { CustomDateOperators } from "./CustomDateOperators";
import { MultiSelectOperator } from "./MultiSelectGridFilter";
import { WorkItem } from "../models/WorkItem";
import { WorkItemPageTypes } from "../models/WorkItemPageTypes";

export const WorkItemGridColumns = (
  typeOfWorkItemPage: string,
  setErrorMessage: Dispatch<SetStateAction<string | null>>,
  refetchData: Function,
  businessFunctions: string[],
  businessProcesses: string[],
  resources: { value: string; label: string }[],
  reasonCodes: string[],
  onRowSelected: (rowId: string, checked: boolean, isHeader: boolean) => void,
  isRowChecked: (rowId: string) => boolean,
  isHeaderChecked: boolean,
  showMassActionIcons: boolean,
  setSnackBarState?: Dispatch<SetStateAction<boolean>>,
  setSnackBarMessage?: Dispatch<SetStateAction<string>>
) => {
  const statuses: { value: string; label: string }[] =
    typeOfWorkItemPage == WorkItemPageTypes.InactivePage
      ? [
          { value: "COMPLETED", label: "Completed" },
          { value: "CANCELLED", label: "Cancelled" },
        ]
      : [
          { value: "IN_PROGRESS", label: "In Progress" },
          { value: "ON_HOLD", label: "On Hold" },
          { value: "NEW", label: "New" },
        ];

  return [
    {
      field: "SelectRows",
      flex: 0.2,
      renderHeader: () => (
        <Checkbox
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onRowSelected("", event.target.checked, true);
          }}
          checked={isHeaderChecked}
          sx={{ mb: "-8px", ml: "-4px", display: "flex", alignItems: "center" }}
        ></Checkbox>
      ),
      renderCell: (params: GridCellParams) => (
        <Checkbox
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            !isHeaderChecked && onRowSelected(params.id.toString(), event.target.checked, false);
          }}
          checked={isHeaderChecked ? true : isRowChecked(params.id.toString())}
          sx={{ ml: "-4px", display: "flex", alignItems: "center" }}
        ></Checkbox>
      ),
      sortable: false,
      filterable: false,
    },
    {
      field: "Associations",
      headerName: "Number",
      flex: 0.9,
      sortable: false,
      hideSortIcons: true,
      filterOperators: CustomFreeFormOperators.filter((operator) => operator.value == "contains"),
      renderCell: (params: GridCellParams) => {
        //TODO: Do we need to check if context of wi is "Loan" ? What if context is not "Loan" ?
        if (params.row.Associations) {
          const associationObject = params.row.Associations[0];
          try {
            const associatedContext = JSON.parse(associationObject.Key);
            return associatedContext.loanIdentifier.toString();
          } catch {
            return "";
          }
        }
      },
    },
    {
      field: "BusinessFunction",
      headerName: "Business Function",
      flex: 1.0,
      valueOptions: businessFunctions,
      filterOperators: [MultiSelectOperator(businessFunctions, "Business Function")],
    },
    {
      field: "Title",
      headerName: "Title",
      flex: 0.5,
      filterOperators: CustomFreeFormOperators,
    },
    {
      field: "Status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 104,
      renderCell: (params: GridCellParams) => (
        <div style={{ whiteSpace: "nowrap", overflow: "visible" }}>
          <WorkItemStatusChip status={params.value as string} />
        </div>
      ),
      valueOptions: () => statuses,
      filterOperators: [MultiSelectOperator(statuses, "Status")],
    },
    {
      field: "BusinessProcess",
      headerName: "Business Process",
      flex: 1.2,
      valueOptions: businessProcesses,
      filterOperators: [MultiSelectOperator(businessProcesses, "Business Process")],
    },
    {
      field: "DueDate",
      headerName: "Due Date",
      flex: 0.4,
      minWidth: 90,
      type: "date",
      filterOperators: CustomDateOperators,
      filterable: typeOfWorkItemPage !== WorkItemPageTypes.InactivePage,
      valueGetter: (params: GridRenderCellParams) => {
        return params.value ? format(parseISO(params.value.toString() ?? ""), "M/d/yyyy") : "";
      },
      renderCell: (params: GridRenderCellParams) =>
        params.row.DueDate ? format(parseISO(params.row.DueDate.toString()), "M/d/yyyy") : "",
    },
    { field: "Priority", headerName: "Priority", flex: 0.5, filterable: false },
    {
      field: "CreatedDatetime",
      headerName: "Created Date",
      flex: 0.9,
      minWidth: 100,
      type: "date",
      filterOperators: CustomDateOperators,
      filterable: typeOfWorkItemPage !== WorkItemPageTypes.InactivePage,
      renderCell: (params: GridRenderCellParams) => {
        return params.row.CreatedDatetime
          ? format(parseISO(params.row.CreatedDatetime.toString()), "M/d/yyyy")
          : "";
      },
      valueGetter: (params: GridRenderCellParams) => {
        return params.value ? format(parseISO(params.value.toString() ?? ""), "M/d/yyyy") : "";
      },
    },
    {
      field: "FollowUpDate",
      headerName: "Follow Up Date",
      flex: 0.8,
      minWidth: 90,
      type: "date",
      filterable: typeOfWorkItemPage !== WorkItemPageTypes.InactivePage,
      filterOperators: CustomDateOperators.filter((operator) => operator.value == "is"),
      renderCell: (params: GridRenderCellParams) =>
        params.row.FollowUpDate ? format(parseISO(params.row.FollowUpDate ?? ""), "M/d/yyyy") : "",

      valueGetter: (params: GridRenderCellParams) => {
        return params?.value ? format(parseISO(params?.value?.toString() ?? ""), "M/d/yyyy") : "";
      },
    },
    {
      field: "AssignedTo",
      headerName: "Assigned To",
      flex: 0.7,
      filterable: typeOfWorkItemPage == WorkItemPageTypes.ActivePage,
      sortable: false,
      hideSortIcons: true,
      filterOperators: [MultiSelectOperator(resources, "Assigned To")],
    },
    {
      field: "ClosedDatetime",
      headerName: "Closed Date",
      flex: 0.9,
      minWidth: 100,
      type: "date",
      filterOperators: CustomDateOperators,
      filterable: typeOfWorkItemPage == WorkItemPageTypes.InactivePage,
      renderCell: (params: GridRenderCellParams) =>
        params.row.ClosedDatetime
          ? format(parseISO(params.row.ClosedDatetime ?? ""), "M/d/yyyy")
          : "",

      valueGetter: (params: GridRenderCellParams) => {
        return params?.value ? format(parseISO(params?.value?.toString() ?? ""), "M/d/yyyy") : "";
      },
    },
    {
      field: "ClosedBy",
      headerName: "Closed By",
      flex: 0.7,
      sortable: false,
      hideSortIcons: true,
      filterable: typeOfWorkItemPage == WorkItemPageTypes.InactivePage,
      filterOperators: [MultiSelectOperator(resources, "Closed By")],
    },
    {
      field: "ResolutionReasonCode",
      headerName: "Resolution Code",
      flex: 1.2,
      filterable: typeOfWorkItemPage == WorkItemPageTypes.InactivePage,
      filterOperators: [MultiSelectOperator(reasonCodes, "Resolution Code")],
    },
    // ==> Invisible cells to render for viewing inactive WI details
    {
      field: "BusinessArea",
      filterable: false,
      renderCell: (params: GridCellParams) => (params.value ? params.value : ""),
    },
    {
      field: "CreatedBy",
      filterable: false,
      renderCell: (params: GridCellParams) => {
        return params.value ? params.value : "";
      },
    },
    {
      field: "Description",
      filterable: false,
      renderCell: (params: GridCellParams) => (params.value ? params.value : ""),
    },
    {
      field: "RowMenu",
      headerName: "",
      maxWidth: 20,
      sortable: false,
      filterable: false,
      renderCell: (params: GridCellParams) => (
        <div style={{ whiteSpace: "nowrap", overflow: "visible" }}>
          <WorkItemRowMenu
            workItemId={params.id.toString()}
            dueDate={params.row.DueDate}
            followUpDate={params.row.FollowUpDate}
            onError={setErrorMessage}
            refetch={refetchData}
            showMassActionIcons={showMassActionIcons}
            useAssignToMe={typeOfWorkItemPage == WorkItemPageTypes.ActivePage}
            useAssignToWorker={typeOfWorkItemPage == WorkItemPageTypes.ActivePage}
            assignedTo={params.row.AssignedToIdentifier}
            status={params.row.Status}
            setSnackBarState={
              typeOfWorkItemPage == WorkItemPageTypes.ActivePage ? setSnackBarState : undefined
            }
            setSnackBarMessage={
              typeOfWorkItemPage == WorkItemPageTypes.ActivePage ? setSnackBarMessage : undefined
            }
            inactiveWIDetails={
              typeOfWorkItemPage == WorkItemPageTypes.InactivePage
                ? ({
                    WorkItemIdentifier: params.id.toString(),
                    Title: params.row.Title,
                    Associations: params.row.Associations,
                    BusinessArea: params.row.BusinessArea,
                    ClosedDatetime: params.row.ClosedDatetime,
                    CreatedDatetime: params.row.CreatedDatetime,
                    AssignedTo: params.row.AssignedTo,
                    CreatedBy: params.row.CreatedBy,
                    Description: params.row.Description,
                  } as WorkItem)
                : undefined
            }
            workItemIsAssigned={params.row.AssignedTo}
          />
        </div>
      ),
    },
  ];
};
